import { useState } from "react"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/modules/shared/ui/components/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shared/ui/components/select"
import { useFolderList } from "../../hooks/useFolders"
import { useBulkMoveDocuments } from "../../hooks/useDocuments"

const ROOT_SENTINEL = "__root__"

interface BulkMoveDocumentsModalProps {
  documentIds: string[]
  onClose: () => void
  onMoved: () => void
}

export function BulkMoveDocumentsModal({
  documentIds,
  onClose,
  onMoved,
}: BulkMoveDocumentsModalProps) {
  const [selectedFolderId, setSelectedFolderId] = useState<string>(ROOT_SENTINEL)

  const { data: folders = [], isLoading: foldersLoading } = useFolderList()
  const { mutateAsync: bulkMove, isPending } = useBulkMoveDocuments()

  const handleConfirm = async () => {
    await bulkMove({
      documentIds,
      folderId: selectedFolderId === ROOT_SENTINEL ? null : selectedFolderId,
    })
    onMoved()
    onClose()
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Move {documentIds.length} Document{documentIds.length !== 1 ? "s" : ""}
          </DialogTitle>
          <DialogDescription>Choose a destination folder.</DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <Select
            value={selectedFolderId}
            onValueChange={setSelectedFolderId}
            disabled={foldersLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a folder..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ROOT_SENTINEL}>
                — Root (no folder) —
              </SelectItem>
              {folders.map((folder) => (
                <SelectItem key={folder.id} value={folder.id}>
                  {folder.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isPending || foldersLoading}>
            {isPending ? "Moving..." : `Move ${documentIds.length}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
